import { onMounted, onUnmounted, ref } from '@vue/composition-api'

// All mounted components that call useUnsavedChangesGuard register here.
// guardRouteLeave chains through all of them so pages with multiple registered
// guards (e.g. a parent that owns the dialog) are handled correctly.
let _activeConfirms = []

// Set to true before a deliberate logout so the beforeunload guard does not
// block the form POST that signs the user out.
let _bypassOnce = false

export function bypassGuardOnce() {
  _bypassOnce = true
}

// Named export for route-level components (ContactViewPage, Default, etc.)
export function guardRouteLeave(next) {
  const fns = [..._activeConfirms]
  if (fns.length === 0) { next(); return }
  const run = async () => {
    for (const fn of fns) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await fn()
      if (!ok) return false
    }

    return true
  }
  run().then(ok => next(ok ? undefined : false))
}

export default function useUnsavedChangesGuard(isDirty) {
  const isGuardDialogOpen = ref(false)
  let _resolve = null

  const confirmIfDirty = () => {
    if (!isDirty.value) return Promise.resolve(true)

    return new Promise(resolve => {
      _resolve = resolve
      isGuardDialogOpen.value = true
    })
  }

  const onGuardConfirmOk = () => {
    isGuardDialogOpen.value = false
    if (_resolve) { _resolve(true); _resolve = null }
  }

  const onGuardConfirmCancel = () => {
    isGuardDialogOpen.value = false
    if (_resolve) { _resolve(false); _resolve = null }
  }

  const handleBeforeUnload = e => {
    if (_bypassOnce) {
      _bypassOnce = false

      return
    }
    if (isDirty.value) { e.preventDefault(); e.returnValue = '' }
  }

  onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    _activeConfirms.push(confirmIfDirty)
  })

  onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    _activeConfirms = _activeConfirms.filter(fn => fn !== confirmIfDirty)
  })

  return {
    isGuardDialogOpen,
    confirmIfDirty,
    onGuardConfirmOk,
    onGuardConfirmCancel,
  }
}
